import { Link, useRouterState } from '@tanstack/react-router';
import { ChevronRight, Home } from 'lucide-react';

export interface BreadcrumbItem {
  label: string;
  href?: string;
  onClick?: () => void;
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const SEGMENT_LABELS: Record<string, string> = {
  dashboard: 'Dashboard',
  permits: 'Permits',
  new: 'New',
  customers: 'Customers',
  users: 'Users',
  roles: 'Roles',
  config: 'Configuration',
  'permit-types': 'Request Types',
  'request-types': 'Request Types',
  'pledge-study': 'Pledge & Study',
  notifications: 'Notifications',
  survey: 'Survey',
  responses: 'Responses',
  documents: 'Documents',
  risks: 'Risk Levels',
  inspection: 'Inspection',
  payment: 'Payment',
  pledge: 'Pledge Study',
  settings: 'Settings',
  mfa: 'MFA',
};

export function segmentLabel(seg: string): string {
  return SEGMENT_LABELS[seg] ?? seg.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function useAutoItems(): BreadcrumbItem[] {
  const { location } = useRouterState();
  const segments = location.pathname.split('/').filter(Boolean);

  // If any segment is a UUID, suppress auto-breadcrumb — DetailPage/Shell handles it
  if (segments.some((s) => UUID_REGEX.test(s))) {
    return [];
  }

  const items: BreadcrumbItem[] = [];
  let path = '';

  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    path += `/${seg}`;

    if (UUID_REGEX.test(seg)) continue;

    const isLast = items.length === segments.filter((s) => !UUID_REGEX.test(s)).length - 1;

    items.push({
      label: segmentLabel(seg),
      href: isLast ? undefined : path,
    });
  }

  return items;
}

export function buildDetailItems(
  pathname: string,
  title: string,
  onBack?: () => void,
): BreadcrumbItem[] {
  const segments = pathname.split('/').filter(Boolean);
  const parentSegments = segments.filter((s) => !UUID_REGEX.test(s));

  const items: BreadcrumbItem[] = [];
  let path = '';
  let segIdx = 0;

  for (const seg of segments) {
    path += `/${seg}`;
    if (UUID_REGEX.test(seg)) continue;

    const isLast = segIdx === parentSegments.length - 1;
    items.push({
      label: segmentLabel(seg),
      href: path,
      onClick: isLast ? onBack : undefined,
    });
    segIdx++;
  }

  items.push({ label: title });
  return items;
}

interface BreadcrumbProps {
  items?: BreadcrumbItem[];
}

export function Breadcrumb({ items }: BreadcrumbProps) {
  const autoItems = useAutoItems();
  const resolved = items ?? autoItems;

  if (resolved.length === 0) return null;

  return (
    <nav className="mb-4 flex items-center gap-1 text-sm" aria-label="Breadcrumb">
      <Link to="/dashboard" className="text-gray-400 hover:text-gray-600 transition-colors">
        <Home size={14} />
      </Link>
      {resolved.map((item, idx) => (
        <span key={idx} className="flex items-center gap-1">
          <ChevronRight size={13} className="text-gray-300" />
          {item.href ? (
            <Link
              to={item.href}
              onClick={item.onClick}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              {item.label}
            </Link>
          ) : item.onClick ? (
            <button
              type="button"
              onClick={item.onClick}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              {item.label}
            </button>
          ) : (
            <span className="font-medium text-gray-700">{item.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}
